import {
  Box,
  ButtonGroup,
  Flex,
  Icon,
  IconButton,
  Table,
  TableProps,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import axios from 'axios';
import { isBefore } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  RiDeleteBinLine,
  RiEyeLine,
  RiLoginCircleLine,
  RiMoneyDollarCircleLine,
} from 'react-icons/ri';
import { ConfirmationModal } from '../../../../../../../../../../../../../../../../../components/ConfirmationModal';
import { LinkButton } from '../../../../../../../../../../../../../../../../../components/LinkButton';
import { BookingPaymentConfirmationModal } from '../../../../../../../../../../../../../../../../../components/BookingPaymentConfirmationModal';
import { useAuth } from '../../../../../../../../../../../../../../../../../hooks/auth';
import { useCalendarEvents } from '../../../../../../../../../../../../../../../../../hooks/calendarEvent';
import {
  IBookingBase,
  IBookingCheckinBase,
  IBookingPaymentBase,
  PrettyBookingPaymentStatus,
} from '../../../../../../../../../../../../../../../../../models/bookings';
import { IFeatureGroupBase } from '../../../../../../../../../../../../../../../../../models/features';
import {
  IUserAssignmentBase,
  IUserBase,
  IUserOnboardBase,
} from '../../../../../../../../../../../../../../../../../models/users';
import cancelBookingsService from '../../../../../../../../../../../../../../../../../services/Bookings/CancelBookingsService';
import {
  createBookingCheckinsService,
  IBookingCheckin,
} from '../../../../../../../../../../../../../../../../../services/Bookings/CreateBookingCheckinsService';
import {
  createBookingExternalPaymentsService,
  IBookingPayment,
} from '../../../../../../../../../../../../../../../../../services/Bookings/CreateBookingExternalPaymentsService';
import { listBookingsService } from '../../../../../../../../../../../../../../../../../services/Bookings/ListBookingsService';
import { getSchedulesAvailableVacancies } from '../../../../../../../../../../../../../../../../../utils/activity-schedule-capacity';
import { translateError } from '../../../../../../../../../../../../../../../../../utils/errors';
import { maskDateTime } from '../../../../../../../../../../../../../../../../../utils/formatters/handleMask';

const getEventAvailabilityState = (
  availableVacancies: number | null,
): { isFull: boolean; title: string } => {
  if (typeof availableVacancies !== 'number') {
    return { isFull: false, title: 'Ilimitado' };
  }

  if (availableVacancies > 0) {
    return { isFull: false, title: `${availableVacancies} vaga(s)` };
  }

  return { isFull: true, title: 'Lotado' };
};

export interface IHandleBookingPayment {
  bookingId: string;
  payment?: IBookingPayment;
}

interface ISchedulebookingCheckin extends IBookingCheckinBase {
  formattedCreatedAt: string;
  user: IUserBase;
}

interface IUserAssignment extends IUserAssignmentBase {
  featureGroup: IFeatureGroupBase;
  host?: IUserBase;
}

interface IEventScheduleBookingUser extends IUserBase {
  userAssignments: IUserAssignment[];
  onboards: IUserOnboardBase[];
}

export interface IScheduleBooking extends IBookingBase {
  payment?: IBookingPaymentBase;
  user: IEventScheduleBookingUser;
  bookedDateTime: Date;
  experience: number;
  checkins: ISchedulebookingCheckin[];
  isCancelable: boolean;
}

export interface ICreateCheckin {
  booking: IScheduleBooking;
  checkin: IBookingCheckin;
}

interface IScheduleBookingsTableProps extends TableProps {
  activityScheduleId: string;
  bookedDate: string;
  eventId: string;
}

export const ScheduleBookingsTable = ({
  activityScheduleId,
  bookedDate,
  eventId,
  ...rest
}: IScheduleBookingsTableProps): JSX.Element => {
  const toast = useToast();
  const {
    assignment: authenticatedAssignment,
    venture: authenticatedVenture,
    experienceLevelsTitleMap,
  } = useAuth();

  const { calendarEvents, handleCalendarEvents } = useCalendarEvents();

  const [scheduleBookings, setScheduleBookings] = useState<IScheduleBooking[]>(
    [],
  );
  const [handlingBooking, setHandlingBooking] = useState<IScheduleBooking>();

  const cancelBookingWarningMessage = useMemo(() => {
    if (!handlingBooking) {
      return undefined;
    }

    if (handlingBooking?.payment?.status === 'PAYMENT_EXTERNAL') {
      return 'Essa reserva foi paga no clube, o reembolso deverá ser feito externamente.';
    }

    return undefined;
  }, [handlingBooking]);

  const [
    isCheckinConfirmationModalVisible,
    setIsCheckinConfirmationModalVisible,
  ] = useState(false);
  const [
    isDeleteConfirmationModalVisible,
    setIsDeleteConfirmationModalVisible,
  ] = useState(false);
  const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);

  const bookingFeatureGroupsFilter = useMemo(
    () =>
      authenticatedAssignment?.featureGroup.features.some(
        (feature) => feature.key === 'NON_LODGER_BOOKINGS_READ_ACCESS',
      )
        ? undefined
        : ['LODGER'],
    [authenticatedAssignment?.featureGroup.features],
  );

  const loadBookings = useCallback(async () => {
    const { items: bookings } = await listBookingsService({
      activityScheduleId,
      bookedDate,
      featureGroups: bookingFeatureGroupsFilter,
    });

    const parsedBookings = bookings.map((booking) => {
      const bookedDateTime = new Date(
        booking?.bookedDate.concat(`T${booking.activitySchedule.startTime}`) ||
          '',
      );

      const isCancelable =
        !booking?.checkins.length &&
        isBefore(new Date(), bookedDateTime) &&
        !booking?.deletedAt;

      return {
        ...booking,
        bookedDateTime,
        checkins: booking.checkins.map((checkin) => ({
          ...checkin,
          formattedCreatedAt: maskDateTime(checkin.createdAt),
        })),
        experience: booking.activitySchedule.minUserExperience,
        isCancelable,
      };
    });

    setScheduleBookings(parsedBookings);
  }, [activityScheduleId, bookedDate, bookingFeatureGroupsFilter]);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  const handleTooggleCheckinConfirmationModal = (): void => {
    setIsCheckinConfirmationModalVisible((prevState) => !prevState);
  };

  const togglePaymentModal = useCallback((booking?: IScheduleBooking) => {
    setHandlingBooking(booking);

    setIsPaymentModalVisible((prevState) => !prevState);
  }, []);

  const handleBookingPayment = useCallback(
    async ({ bookingId, payment }: IHandleBookingPayment) => {
      try {
        await createBookingExternalPaymentsService({
          bookingId,
          payment,
        });

        loadBookings();

        togglePaymentModal(undefined);

        toast({
          title: 'Transação concluída com sucesso',
          description: 'A transação foi efetuada corretamente.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          if (err.response?.data.message === 'user-address-missing') {
            toast({
              title: 'Cadastro incompleto',
              description: `Para registrar o pagamento será necessário completar o cadastro do membro. ${translateError(
                { message: err.response?.data.message },
              )}`,
              status: 'warning',
              duration: 3000,
              isClosable: true,
              variant: 'subtle',
              position: 'top-right',
            });

            return;
          }

          toast({
            title: 'Falha na transação',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao efetuar a transação, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    },
    [loadBookings, toast, togglePaymentModal],
  );

  const toggleDeleteConfirmationModal = (booking?: IScheduleBooking): void => {
    setHandlingBooking(booking);

    setIsDeleteConfirmationModalVisible((prevState) => !prevState);
  };

  const handlePayment = (booking: IScheduleBooking): void => {
    setHandlingBooking(booking);

    togglePaymentModal(booking);
  };

  const handleCheckin = (booking: IScheduleBooking): void => {
    setHandlingBooking(booking);

    handleTooggleCheckinConfirmationModal();
  };

  const handleCreateCheckin = async (): Promise<void> => {
    if (handlingBooking) {
      try {
        await createBookingCheckinsService(handlingBooking.id);

        loadBookings();

        handleTooggleCheckinConfirmationModal();
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao cadastrar',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao cadastrar o checkin, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    }
  };

  const deleteBooking = async (
    booking: IScheduleBooking,
    reason?: string,
  ): Promise<void> => {
    try {
      await cancelBookingsService({ bookingId: booking.id, reason });

      handleCalendarEvents(
        calendarEvents.map((calendarEvent) => {
          const schedules = calendarEvent.schedules.map((eventSchedule) => {
            const bookings = eventSchedule.bookings.filter(
              (scheduleBooking) => scheduleBooking.id !== booking.id,
            );

            if (
              eventSchedule.activityScheduleId === booking.activityScheduleId
            ) {
              const response = {
                ...eventSchedule,
                booked: bookings.length,
                bookings,
              };

              if (
                booking.user.userAssignments[0].featureGroup.key.includes(
                  'GUEST',
                )
              ) {
                response.guestBooked -= 1;
              }

              if (
                booking.user.userAssignments[0].featureGroup.key.includes(
                  'LODGER',
                )
              ) {
                response.lodgerBooked -= 1;
              }

              return response;
            }

            return eventSchedule;
          });

          const { isFull, title } = getEventAvailabilityState(
            getSchedulesAvailableVacancies(schedules),
          );

          if (calendarEvent.id === eventId) {
            return {
              ...calendarEvent,
              title,
              isFull,
              schedules,
            };
          }

          return calendarEvent;
        }),
      );

      loadBookings();

      toast({
        title: 'Cancelada com sucesso',
        description: 'A reserva foi cancelada corretamente.',
        status: 'success',
        duration: 3000,
        isClosable: true,
        variant: 'subtle',
        position: 'top-right',
      });
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status !== 401) {
        toast({
          title: 'Falha ao cancelar',
          description:
            translateError({ message: err.response?.data.message }) ||
            'Ocorreu um erro ao cancelar a reserva, tente novamente.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });
      }
    } finally {
      toggleDeleteConfirmationModal(undefined);
    }
  };

  return (
    <Box>
      {handlingBooking && (
        <ConfirmationModal
          isOpen={isCheckinConfirmationModalVisible}
          onClose={handleTooggleCheckinConfirmationModal}
          onConfirm={handleCreateCheckin}
          title="Confirmar checkin"
          message="Deseja realmente criar um checkin para essa reserva?"
        />
      )}

      {!!handlingBooking?.payment?.status && (
        <BookingPaymentConfirmationModal
          isOpen={isPaymentModalVisible}
          onClose={() => togglePaymentModal(undefined)}
          onConfirm={(payment) =>
            handleBookingPayment({
              bookingId: handlingBooking.id,
              payment,
            })
          }
          payAfterDisabled={
            handlingBooking.payment.status === 'PAYMENT_EXTERNAL_PENDING'
          }
          user={handlingBooking.user}
        />
      )}

      {!!handlingBooking && (
        <ConfirmationModal
          isOpen={!!isDeleteConfirmationModalVisible}
          onClose={() => toggleDeleteConfirmationModal(undefined)}
          onConfirm={(reason) => deleteBooking(handlingBooking, reason)}
          title="Confirmar cancelamento"
          message="Deseja realmente cancelar?"
          reasonAlert="Motivo requerido, ultrapassou tempo mínimo para cancelamento antes do início do evento"
          isReasonRequired={handlingBooking.exceededCancellationDeadline}
          showReasonInput
          warningMessage={cancelBookingWarningMessage}
        />
      )}

      {scheduleBookings.length ? (
        <Table colorScheme="blue" {...rest}>
          <Thead>
            <Tr>
              <Th>Nome</Th>

              <Th>Nível reservado</Th>

              <Th>Status pagamento</Th>

              <Th>Checkins</Th>

              <Th textAlign="right" w="8" />
            </Tr>
          </Thead>

          <Tbody>
            {scheduleBookings.map((scheduleBooking) => {
              const isPaymentButtonDisabled =
                !scheduleBooking.payment ||
                ![
                  'WAITING',
                  'FAILED',
                  'REJECTED',
                  'PAYMENT_EXTERNAL_PENDING',
                ].includes(scheduleBooking.payment.status) ||
                !authenticatedAssignment?.featureGroup.features.some(
                  (feature) =>
                    [
                      'BOOKING_PAYMENT_FULL_ACCESS',
                      'BOOKING_PAYMENT_WRITE_ACCESS',
                    ].includes(feature.key),
                );

              let paymentButtonColorScheme = isPaymentButtonDisabled
                ? 'green'
                : 'red';

              if (
                scheduleBooking.payment?.status === 'PAYMENT_EXTERNAL_PENDING'
              ) {
                paymentButtonColorScheme = 'yellow';
              }

              const userOnboard = scheduleBooking.user.onboards.find(
                (onboard) => onboard.ventureId === authenticatedVenture?.id,
              );

              return (
                <Tr key={scheduleBooking.id}>
                  <Td>
                    <Box>
                      <Text fontWeight="bold">{scheduleBooking.user.name}</Text>

                      <Text textColor="gray.400">
                        {
                          scheduleBooking.user.userAssignments[0].featureGroup
                            ?.name
                        }
                      </Text>

                      <Text textColor="gray.400" fontSize="sm">
                        {scheduleBooking.user.userAssignments[0].host?.name}
                      </Text>
                    </Box>
                  </Td>

                  <Td>
                    {userOnboard?.onboardType === 'COMPLETED'
                      ? experienceLevelsTitleMap.get(userOnboard.experience)
                      : 'Não avaliado'}
                  </Td>

                  <Td>
                    {scheduleBooking.payment
                      ? PrettyBookingPaymentStatus[
                          scheduleBooking.payment.status
                        ]
                      : 'Isento'}
                  </Td>

                  <Td>
                    {scheduleBooking.checkins.map((checkin) => (
                      <Text fontSize="sm">{`${checkin.user.name} - ${checkin.formattedCreatedAt}`}</Text>
                    ))}
                  </Td>

                  <Td textAlign="right">
                    <ButtonGroup>
                      {!scheduleBooking.deletedAt &&
                        (!scheduleBooking.payment ||
                          [
                            'APPROVED',
                            'PAYMENT_EXTERNAL_PENDING',
                            'PAYMENT_EXTERNAL',
                            'PAYMENT_HOTEL',
                          ].includes(scheduleBooking.payment.status)) &&
                        authenticatedAssignment?.featureGroup.features.some(
                          (feature) =>
                            [
                              'BOOKING_CHECKIN_FULL_ACCESS',
                              'BOOKING_CHECKIN_READ_ACCESS',
                              'BOOKING_CHECKIN_WRITE_ACCESS',
                            ].includes(feature.key),
                        ) && (
                          <IconButton
                            aria-label="Checkins"
                            size="sm"
                            icon={<Icon as={RiLoginCircleLine} fontSize="16" />}
                            fontSize="16"
                            colorScheme="green"
                            onClick={() => handleCheckin(scheduleBooking)}
                          />
                        )}

                      {scheduleBooking.payment &&
                        authenticatedAssignment?.featureGroup.features.some(
                          (feature) =>
                            [
                              'BOOKING_PAYMENT_FULL_ACCESS',
                              'BOOKING_PAYMENT_WRITE_ACCESS',
                            ].includes(feature.key),
                        ) && (
                          <IconButton
                            aria-label="Payment"
                            size="sm"
                            icon={
                              <Icon
                                as={RiMoneyDollarCircleLine}
                                fontSize="16"
                              />
                            }
                            fontSize="16"
                            colorScheme={paymentButtonColorScheme}
                            color="white"
                            disabled={isPaymentButtonDisabled}
                            onClick={() => handlePayment(scheduleBooking)}
                          />
                        )}

                      <LinkButton
                        px={0}
                        to={{
                          pathname: '/bookings/details',
                          state: {
                            bookingId: scheduleBooking.id,
                          },
                        }}
                      >
                        <Icon as={RiEyeLine} fontSize="16" />
                      </LinkButton>

                      {scheduleBooking.isCancelable &&
                        authenticatedAssignment?.featureGroup.features.some(
                          (feature) =>
                            [
                              'BOOKING_FULL_ACCESS',
                              'BOOKING_DELETE_ACCESS',
                            ].includes(feature.key),
                        ) && (
                          <IconButton
                            aria-label="delete-booking"
                            size="sm"
                            icon={<Icon as={RiDeleteBinLine} fontSize="16" />}
                            colorScheme="red"
                            onClick={() =>
                              toggleDeleteConfirmationModal(scheduleBooking)
                            }
                          />
                        )}
                    </ButtonGroup>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      ) : (
        <Flex w="full" h="24" justify="center" align="center">
          <Text color="gray.600">Nenhuma reserva na para esse dia</Text>
        </Flex>
      )}
    </Box>
  );
};
